import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getCategoryBySlug } from "@/mocks/categories";
import { getMangaBySlug } from "@/mocks/comics";
import InfoPanel from "./components/InfoPanel";
import ChapterList from "./components/ChapterList";
import Reader from "./components/Reader";
import RelatedSection from "./components/RelatedSection";

const SITE_BASE = "https://kisscorner.github.io";

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const requestedCh = searchParams.get("ch") || "";
  const comic = getMangaBySlug(slug);

  const [activeSlug, setActiveSlug] = useState<string>("");

  useEffect(() => {
    if (!comic) return;
    const target = comic.chapters.find((c) => c.slug === requestedCh)?.slug || comic.chapters[0]?.slug || "";
    setActiveSlug(target);
  }, [comic, requestedCh]);

  const activeIndex = comic ? comic.chapters.findIndex((c) => c.slug === activeSlug) : -1;
  const activeChapter = activeIndex >= 0 ? comic?.chapters[activeIndex] : undefined;

  const scrollToReader = useCallback(() => {
    window.setTimeout(() => {
      document.getElementById("reader")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }, []);

  const selectChapter = (chapterSlug: string) => {
    setActiveSlug(chapterSlug);
    scrollToReader();
  };

  const goPrev = () => {
    if (!comic || activeIndex <= 0) return;
    setActiveSlug(comic.chapters[activeIndex - 1].slug);
    scrollToReader();
  };

  const goNext = () => {
    if (!comic || activeIndex < 0 || activeIndex >= comic.chapters.length - 1) return;
    setActiveSlug(comic.chapters[activeIndex + 1].slug);
    scrollToReader();
  };

  usePageMeta({
    title: comic
      ? `${comic.title} 漫画在线免费阅读 - 89890漫画网`
      : "漫画不存在 - 89890漫画网",
    description: comic
      ? `${comic.description} 在89890漫画网免费在线阅读《${comic.title}》全部章节与大结局。`
      : "抱歉，您访问的漫画不存在。",
    keywords: comic
      ? `${comic.title},${comic.tags.join(",")},免费阅读,在线看漫画,89890漫画网`
      : "89890漫画网",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: comic ? `${comic.title} 漫画在线免费阅读` : "漫画不存在",
        url: `${SITE_BASE}/article/${slug}`,
        isPartOf: { "@id": `${SITE_BASE}/#website` },
        inLanguage: "zh-CN",
      },
      ...(comic
        ? [
            {
              "@context": "https://schema.org",
              "@type": "ComicSeries",
              name: comic.title,
              url: `${SITE_BASE}/article/${comic.slug}`,
              image: comic.cover,
              author: { "@type": "Person", name: comic.author },
              description: comic.longDescription,
              inLanguage: "zh-CN",
              isFamilyFriendly: true,
              hasPart: comic.chapters.map((chapter, index) => ({
                "@type": "ComicIssue",
                name: chapter.title,
                position: index + 1,
                url: `${SITE_BASE}/article/${comic.slug}?ch=${chapter.slug}`,
                isAccessibleForFree: true,
              })),
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_BASE}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: `${getCategoryBySlug(comic.categorySlugs[0])?.name || ""}漫画`,
                  item: `${SITE_BASE}/category/${comic.categorySlugs[0]}`,
                },
                { "@type": "ListItem", position: 3, name: comic.title, item: `${SITE_BASE}/article/${comic.slug}` },
              ],
            },
          ]
        : []),
    ],
  });

  if (!comic || !activeChapter) {
    return (
      <div className="container-site flex min-h-[60vh] flex-col items-center justify-center pt-16 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">抱歉，这部漫画不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">您可以返回首页或浏览其它分类继续寻找喜欢的作品。</p>
        <Link to="/category/all" className="btn-primary mt-6">
          浏览全部漫画
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: `${getCategoryBySlug(comic.categorySlugs[0])?.name || ""}漫画`, to: `/category/${comic.categorySlugs[0]}` },
          { label: comic.title },
        ]}
      />

      <section className="container-site">
        <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-6 md:p-8">
          <InfoPanel comic={comic} onStartReading={scrollToReader} />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ChapterList comic={comic} activeSlug={activeChapter.slug} onSelect={selectChapter} />
            <div className="mt-8">
              <Reader
                comic={comic}
                chapter={activeChapter}
                hasPrev={activeIndex > 0}
                hasNext={activeIndex < comic.chapters.length - 1}
                onPrev={goPrev}
                onNext={goNext}
              />
            </div>
          </div>
          <aside className="space-y-6">
            <RelatedSection comic={comic} />
          </aside>
        </div>
      </section>
    </div>
  );
}